/* TLR — infinite scroll (assets/tlr-infinite-scroll.js)

   Progressive enhancement over the existing {% paginate %} markup. Enabled by the
   merchant in the theme editor (Collection page → Browsing). The snippet still renders
   the real pagination links inside itself; this hides them and appends the next page
   in place as the shopper reaches the end. With JS off the links stay put and the
   collection remains fully navigable.

   Two integration constraints drive the design:

   1. section-main-collection.js replaces #CollectionProductGrid's innerHTML wholesale
      whenever a filter or sort changes. Any node reference we cached is dead after that,
      and our own appended pages must be discarded (they belong to the old filter). So we
      hold NO long-lived element references — every run re-queries from the container —
      and a MutationObserver re-initialises us when the grid is swapped underneath.

   2. Appended cards need the theme's own behaviours bound (quickshop, grid video).
      WAU exposes those as idempotent .init() calls; we invoke them after each append.
*/
(function () {
  'use strict';

  // After this many auto-appends we stop observing and require a click. Keeps the
  // footer reachable and stops runaway loading on very large collections.
  var AUTO_LOAD_LIMIT = 3;
  // Retries for a throttled (429) page fetch, with exponential backoff.
  var MAX_RETRIES = 3;
  // Quickshop prefetches are staggered this far apart so a freshly appended batch
  // cannot fire ~18 parallel section requests and trip Shopify's rate limit.
  var PREFETCH_STAGGER_MS = 300;

  function InfiniteScroll(root) {
    this.root = root;
    this.autoLoads = 0;
    this.retries = 0;
    this.loading = false;
    this.nextUrl = root.dataset.nextUrl || '';
    this.gridSelector = root.dataset.grid || '.product-loop';
    // Item + pagination markup differ per section (legacy main-collection vs the TLR
    // grid), so both are passed in; the defaults keep main-collection working untouched.
    this.itemSelector = root.dataset.item || '.product-loop__item';
    this.pageLinkSelector = root.dataset.pageLinks || '.pagination__wrapper a[href]';
    this.total = parseInt(root.dataset.total, 10) || 0;
    this.pageSize = parseInt(root.dataset.pageSize, 10) || 0;
    this.currentPage = parseInt(root.dataset.currentPage, 10) || 1;
    this.totalPages = parseInt(root.dataset.totalPages, 10) || 1;

    this.el = {
      fallback: root.querySelector('[data-tlr-infinite-fallback]'),
      count: root.querySelector('[data-tlr-infinite-count]'),
      live: root.querySelector('[data-tlr-infinite-live]'),
      spinner: root.querySelector('[data-tlr-infinite-spinner]'),
      more: root.querySelector('[data-tlr-infinite-more]'),
      error: root.querySelector('[data-tlr-infinite-error]'),
      sentinel: root.querySelector('[data-tlr-infinite-sentinel]')
    };

    this.init();
  }

  InfiniteScroll.prototype.init = function () {
    var self = this;

    // JS is running, so the server-rendered pagination links are redundant as a
    // control — hide them but leave them in the DOM as our source for next URLs.
    if (this.el.fallback) this.el.fallback.hidden = true;

    if (this.el.more) {
      this.el.more.addEventListener('click', function () {
        self.autoLoads = 0;          // a deliberate click re-arms auto-loading
        self.loadNext();
      });
    }

    this.updateCount();
    if (this.nextUrl) this.observe();
    else this.finish();
  };

  /* --- scroll detection ------------------------------------------------- */

  InfiniteScroll.prototype.observe = function () {
    var self = this;
    if (!this.el.sentinel || !('IntersectionObserver' in window)) {
      // No IO support: fall back to a manual button rather than scroll handlers.
      this.showMoreButton();
      return;
    }
    if (this.io) return;

    this.io = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      if (self.autoLoads >= AUTO_LOAD_LIMIT) { self.showMoreButton(); return; }
      self.autoLoads++;
      self.loadNext();
    }, { rootMargin: '600px 0px' });   // start fetching before the shopper hits the end

    this.io.observe(this.el.sentinel);
  };

  InfiniteScroll.prototype.unobserve = function () {
    if (this.io) { this.io.disconnect(); this.io = null; }
  };

  /* --- fetching --------------------------------------------------------- */

  InfiniteScroll.prototype.loadNext = function () {
    var self = this;
    if (this.loading || !this.nextUrl) return;

    this.loading = true;
    this.hide(this.el.more);
    this.hide(this.el.error);
    this.show(this.el.spinner);
    this.unobserve();   // avoid stacking requests while one is in flight

    fetch(this.nextUrl)
      .then(function (r) {
        // 429 is expected on this storefront: the theme's quickshop prefetcher fires a
        // burst of section requests for every card that scrolls into view, and our page
        // fetch lands in the same throttle window. It is transient, so back off and
        // retry rather than dead-ending the shopper. Honour Retry-After when sent.
        if (r.status === 429 || r.status === 503) {
          var wait = parseFloat(r.headers.get('Retry-After')) * 1000;
          if (!wait || isNaN(wait)) wait = Math.min(1000 * Math.pow(2, self.retries), 8000);
          throw { retry: true, wait: wait };
        }
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        self.retries = 0;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        self.append(doc);
      })
      .catch(function (err) {
        if (err && err.retry && self.retries < MAX_RETRIES) {
          self.retries++;
          self.loading = false;
          setTimeout(function () { self.loadNext(); }, err.wait);
          return;
        }
        self.fail();
      });
  };

  InfiniteScroll.prototype.append = function (doc) {
    var grid = this.root.closest('#CollectionProductGrid') ||
               this.root.closest('[data-tlr-results]') || document;
    var target = grid.querySelector(this.gridSelector) ||
                 document.querySelector(this.gridSelector);
    var incoming = doc.querySelector(this.gridSelector);

    if (!target || !incoming) { this.fail(); return; }

    var items = incoming.querySelectorAll(this.itemSelector);
    if (!items.length) { this.finish(); return; }

    // Cards carry animate--text + delay--N classes for the reveal animation. Those
    // delays are indexed per server-rendered page, so appended rows would replay a
    // staggered fade mid-scroll. Strip the delay and let them appear immediately.
    var frag = document.createDocumentFragment();
    var added = [];
    Array.prototype.forEach.call(items, function (item) {
      var node = document.importNode(item, true);
      node.className = node.className.replace(/\bdelay--\d+\b/g, '');
      node.classList.add('is-visible', 'animated');
      added.push(node);
      frag.appendChild(node);
    });
    target.appendChild(frag);

    this.currentPage++;
    // Read the *fetched* document's next link rather than building a URL ourselves —
    // that keeps every active filter/sort param intact.
    this.nextUrl = this.resolveNext(doc);

    this.loading = false;
    this.hide(this.el.spinner);
    this.updateCount();
    this.rebind(target, added);
    this.syncUrl();

    if (this.nextUrl) {
      if (this.autoLoads >= AUTO_LOAD_LIMIT) this.showMoreButton();
      else this.observe();
    } else {
      this.finish();
    }
  };

  /* Find the next-page URL in a fetched page. The theme's snip-pagination marks the
     next control with an aria-label, but that label is translated, so match on the
     page number instead: the link whose page= is one past the page we just added. */
  InfiniteScroll.prototype.resolveNext = function (doc) {
    var wanted = this.currentPage + 1;
    var links = doc.querySelectorAll(this.pageLinkSelector);
    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href') || '';
      var m = href.match(/[?&]page=(\d+)/);
      if (m && parseInt(m[1], 10) === wanted) return href;
    }
    return '';
  };

  /* Keep the address bar on the last page appended, so a refresh or a share lands
     the shopper near where they were instead of back at page 1. replaceState, not
     push — infinite scroll should not manufacture history entries. */
  InfiniteScroll.prototype.syncUrl = function () {
    try {
      var url = new URL(window.location.href);
      if (this.currentPage > 1) url.searchParams.set('page', this.currentPage);
      else url.searchParams.delete('page');
      history.replaceState(history.state, '', url.toString());
    } catch (e) { /* non-fatal */ }
  };

  /* Wire the theme's behaviours onto the newly added cards.

     We deliberately do NOT call WAU.Quickshop.init(). That function walks EVERY
     quickview button on the page and attaches an IntersectionObserver that prefetches
     the product's ?section_id=quickshop HTML the moment the card nears the viewport
     (product-quickshop.js:65-76). Calling it after an append re-observes the whole
     grid and fires ~18 parallel section requests at once, which trips Shopify's rate
     limit — the 429 storm that also takes down our own page fetch.

     Instead we bind only the cards we just added, and stagger their prefetches. */
  InfiniteScroll.prototype.rebind = function (container, added) {
    var self = this;

    try {
      if (window.WAU && WAU.ProductGridVideo && WAU.ProductGridVideo.init) {
        WAU.ProductGridVideo.init();
      }
    } catch (e) {}

    this.bindQuickshop(added || []);

    // Let apps (reviews, labels, wishlists) re-scan the appended nodes.
    document.dispatchEvent(new CustomEvent('tlr:products-appended', {
      bubbles: true,
      detail: { container: container, items: added || [] }
    }));
  };

  /* The theme binds quickview clicks directly to each button inside init() rather than
     delegating, so appended cards need their own listener or the button does nothing.
     We attach the same click behaviour here, then observe for prefetch separately. */
  InfiniteScroll.prototype.bindQuickshop = function (nodes) {
    if (!window.WAU || !WAU.Quickshop) return;
    var self = this;

    nodes.forEach(function (node) {
      var buttons = node.querySelectorAll('.js-quickview-trigger');
      Array.prototype.forEach.call(buttons, function (button) {
        if (button.__tlrQuickshopBound) return;
        button.__tlrQuickshopBound = true;

        button.addEventListener('click', function (event) {
          event.preventDefault();
          event.target.classList.add('loading-quickshop');

          var productUrl = WAU.Quickshop.cleanUrl(this.dataset.productUrl, 'variant');
          var singleVariant = event.target.hasAttribute('data-quick-add');
          var loader = new WAU.Helpers.scriptLoader();

          if (this.dataset.hasModel) loader.load([jsAssets.productModel]).finally(function () {});
          if (this.dataset.hasVideo) loader.load([jsAssets.productVideo]).finally(function () {});

          loader.load([jsAssets.flickity]).finally(function () {
            loader.load([jsAssets.product]).finally(function () {
              WAU.Quickshop.getTemplate(productUrl, singleVariant, event.target);
            });
          });
        });

        // Prefetch on approach — queued, so a screenful of new cards trickles
        // instead of firing ~18 section requests in parallel.
        if (!WAU.Quickshop.prefetchQuickview || !('IntersectionObserver' in window)) return;
        var io = new IntersectionObserver(function (entries, obs) {
          if (!entries[0].isIntersecting) return;
          obs.unobserve(button);
          self.queuePrefetch(button);
        }, { rootMargin: '0px 0px 200px 0px' });
        io.observe(button);
      });
    });
  };

  /* Serialised prefetch queue. One request in flight at a time with a gap between
     them, so a screenful of new cards trickles instead of stampeding. */
  InfiniteScroll.prototype.queuePrefetch = function (button) {
    if (!this._queue) this._queue = [];
    this._queue.push(button);
    this.drainPrefetch();
  };

  InfiniteScroll.prototype.drainPrefetch = function () {
    var self = this;
    if (this._draining || !this._queue || !this._queue.length) return;
    this._draining = true;

    var button = this._queue.shift();
    Promise.resolve()
      .then(function () { return WAU.Quickshop.prefetchQuickview(button); })
      .catch(function () { /* a failed prefetch just means no warm cache */ })
      .then(function () {
        setTimeout(function () {
          self._draining = false;
          self.drainPrefetch();
        }, PREFETCH_STAGGER_MS);
      });
  };

  /* --- status helpers --------------------------------------------------- */

  InfiniteScroll.prototype.updateCount = function () {
    var shown = document.querySelectorAll(this.gridSelector + ' ' + this.itemSelector).length;
    if (!this.el.count) return;
    var tpl = this.root.dataset.textShowing || 'Showing [shown] of [total]';
    var text = tpl.replace('[shown]', shown).replace('[total]', this.total);
    this.el.count.textContent = text;
    if (this.el.live) this.el.live.textContent = text;
  };

  InfiniteScroll.prototype.showMoreButton = function () {
    this.unobserve();
    if (this.nextUrl) this.show(this.el.more);
  };

  InfiniteScroll.prototype.finish = function () {
    this.loading = false;
    this.nextUrl = '';
    this.unobserve();
    this.hide(this.el.spinner);
    this.hide(this.el.more);
  };

  InfiniteScroll.prototype.fail = function () {
    this.loading = false;
    this.retries = 0;   // a manual retry gets a fresh budget
    this.hide(this.el.spinner);
    if (this.el.error) {
      this.el.error.textContent = this.root.dataset.textError || 'Could not load more products.';
      this.show(this.el.error);
    }
    // Leave a way forward rather than dead-ending the shopper.
    this.showMoreButton();
  };

  InfiniteScroll.prototype.show = function (el) { if (el) el.hidden = false; };
  InfiniteScroll.prototype.hide = function (el) { if (el) el.hidden = true; };

  /* --- boot ------------------------------------------------------------- */

  function boot() {
    var roots = document.querySelectorAll('[data-tlr-infinite]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__tlrInfinite) return;      // already wired
      root.__tlrInfinite = new InfiniteScroll(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Filters/sort replace #CollectionProductGrid's innerHTML, which destroys our
     root along with any appended pages. Watch the container and re-boot against
     the freshly rendered markup — which correctly reflects page 1 of the new
     filter, so no stale state carries over. */
  var host = document.getElementById('CollectionProductGrid');
  if (host && 'MutationObserver' in window) {
    new MutationObserver(function () { boot(); })
      .observe(host, { childList: true, subtree: true });
  }
})();
